import { FormEvent, useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { KeepoLoader } from "../../../../../../components/KeepoLoader";
import { LoadingModal } from "../../../../../../components/LoadingModal";
import Modal, { ModalSecondaryButton } from "../../../../../../components/Modal";
import { useImageUpload } from "../../../../../../hooks/ImageUpload";
import { api, type PexelsItem } from "../../../../../../services/api";
import { BackgroundVideoError, uploadBackgroundVideo } from "../../../../../../utils/backgroundVideo";
import { classNames } from "../../../../../../utils/styles";
import { Panel, Powered } from "./styles";

const prefix = "pages.editor.settings.groups.backgroundMedia";

/** The editor's languages as Pexels names them: a search is understood in the language it's written in. */
const PEXELS_LOCALES: Record<string, string> = {
  pt: "pt-BR",
  en: "en-US",
  es: "es-ES",
  fr: "fr-FR",
  de: "de-DE",
  it: "it-IT",
};

/** Pexels' results a row (the API brings 40 a page: 10 full rows). */
const PER_ROW = 4;

type Kind = "image" | "video";

const IMAGE_TYPES = ["image/png", "image/jpeg", "image/webp"];
const VIDEO_TYPES = ["video/mp4", "video/webm"];
type Step = "choose" | "upload" | "pexels";

/** What was chosen: the file in our bucket (and a video's poster), and whether it's an image or a video. */
export type BackgroundMedia = { url: string; poster?: string; type?: Kind };

/** The top's cover: wide, 1200 x 400 (one's own image and Pexels' are cropped to it). */
const COVER_CROP = { size: { width: 1200, height: 400 }, slice: { aspect: 3 }, cropShape: "rect" as const };

/** Topo › Revista's cover: taller, 1200 x 900 (the name goes over its foot). */
const MAGAZINE_CROP = { size: { width: 1200, height: 900 }, slice: { aspect: 4 / 3 }, cropShape: "rect" as const };

type Props = {
  kind: Kind;
  siteId: string;
  onDone: (media: BackgroundMedia) => void;
  onExit: () => void;
  /** What Pexels opens searched for (the Keepo's field of work); none: its popular photos. */
  search?: string;
  /**
   * The top's cover (Topo › Capa): three ways in (one's own file, an image or a video from Pexels), everything lying
   * down; `kind` is then only where it starts.
   */
  cover?: boolean;
  /** The cover is Revista's: cropped taller. */
  magazine?: boolean;
};

/** Pexels' mark (their green square with the "P"), as they ask to credit them. */
export const PexelsLogo = ({ size = 24 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 32 32" role="img" aria-label="Pexels">
    <rect width="32" height="32" rx="6" fill="#07A081" />
    <path
      fill="#FFFFFF"
      fillRule="evenodd"
      d="M11 7.5h6.75a5.75 5.75 0 0 1 0 11.5H15v5.5h-4zM15 11.5v3.5h2.75a1.75 1.75 0 0 0 0-3.5z"
    />
  </svg>
);

/** 12 → "0:12" */
const duration = (seconds = 0) => `${Math.floor(seconds / 60)}:${String(Math.round(seconds % 60)).padStart(2, "0")}`;

/** A file downloaded from Pexels (their files allow it), to go through the same path as one uploaded. */
const download = async (url: string, name: string, fallbackType: string) => {
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Pexels answered ${response.status}`);
  }
  const blob = await response.blob();
  return new File([blob], name, { type: blob.type || fallbackType });
};

/**
 * A background's image or video: one's own (dropped or picked) or a free one from Pexels (searched, cropped when
 * it's a photo, then sent to our bucket like any other).
 */
export const BackgroundMediaModal = ({ kind: startKind, siteId, onDone, onExit, search, cover, magazine }: Props) => {
  const { t } = useTranslation();
  const [step, setStep] = useState<Step>("choose");
  // what's being added: the cover's Pexels choice, or one's own file's (an image or a video)
  const [kind, setKind] = useState<Kind>(startKind);
  // the texts' key: the cover's own, else the image's or the video's
  const texts = cover && step !== "pexels" ? "cover" : kind;
  const [error, setError] = useState<string>();
  const [sendingVideo, setSendingVideo] = useState(false);
  // a Pexels photo being downloaded (before it goes up): taps meanwhile do nothing
  const [downloading, setDownloading] = useState(false);

  const coverCrop = magazine ? MAGAZINE_CROP : COVER_CROP;
  // one's own image goes whole as a background (as always), cropped wide as a cover; one from Pexels is cropped first
  const ownImage = useImageUpload({
    siteId,
    ...(cover && { crop: coverCrop }),
    onUpload: url => onDone({ url, type: "image" }),
  });
  const pexelsImage = useImageUpload({
    siteId,
    crop: cover
      ? coverCrop
      : { size: { width: 0, height: 0 }, slice: { aspect: 9 / 16 }, cropShape: "rect", cropMode: "free" },
    onUpload: url => onDone({ url, type: "image" }),
  });

  // nothing closes the modal while a file goes up (a late answer would change a background that was given up)
  const busy = ownImage.isUploading || pexelsImage.isUploading || sendingVideo || downloading;

  const sendVideo = async (file: File) => {
    setError(undefined);
    setSendingVideo(true);
    try {
      onDone({ ...(await uploadBackgroundVideo(file, siteId)), type: "video" });
    } catch (reason) {
      const why = reason instanceof BackgroundVideoError ? reason.reason : "upload";
      setError(t(`pages.editor.settings.groups.backgroundFields.video.errors.${why}`));
    } finally {
      setSendingVideo(false);
    }
  };

  const sendOwn = async (file?: File) => {
    if (!file) return;
    setError(undefined);
    // a dropped file skips the input's `accept`
    const accepted = cover ? [...IMAGE_TYPES, ...VIDEO_TYPES] : kind === "video" ? VIDEO_TYPES : IMAGE_TYPES;
    if (!accepted.includes(file.type)) {
      setError(t(`${prefix}.typeError.${cover ? "cover" : kind}`));
      return;
    }
    // the cover takes either: a video goes as one
    if (cover ? file.type.startsWith("video/") : kind === "video") {
      return sendVideo(file);
    }
    try {
      await ownImage.upload(file);
    } catch {
      setError(t(`${prefix}.uploadError`));
    }
  };

  const add = async (item: PexelsItem) => {
    if (busy) return;
    setError(undefined);
    try {
      const video = kind === "video";
      setDownloading(true);
      const file = await download(
        (video ? item.file : item.image) as string,
        `pexels-${item.id}.${video ? "mp4" : "jpg"}`,
        video ? "video/mp4" : "image/jpeg"
      ).finally(() => setDownloading(false));
      if (video) {
        return await sendVideo(file);
      }
      await pexelsImage.upload(file);
    } catch {
      setSendingVideo(false);
      setError(t(`${prefix}.downloadError`));
    }
  };

  const title = step === "pexels" ? t(`${prefix}.pexelsTitle.${kind}`) : t(`${prefix}.title.${texts}`);
  // what the step is for, under the title
  const subtitle = t(`${prefix}.subtitle.${step}.${texts}`);
  const openPexels = (each: Kind) => {
    setKind(each);
    setStep("pexels");
  };

  const back = () => {
    setError(undefined);
    setStep("choose");
  };

  return (
    <>
      {/* the editor's modal: 480px to choose or upload, 880px for Pexels' grid (scrolling under the header) */}
      <Modal
        title={title}
        subtitle={subtitle}
        onExit={onExit}
        escapeExit
        busy={busy}
        exitLabel={t(`${prefix}.close`)}
        size={step === "pexels" ? "lg" : "md"}
        divided={step === "pexels"}
        error={error}
        footer={
          step !== "choose" && (
            <>
              {/* Pexels' credit, always in sight */}
              {step === "pexels" && (
                <Powered href="https://www.pexels.com" target="_blank" rel="noreferrer">
                  <PexelsLogo size={20} />
                  <span>{t(`${prefix}.poweredBy`)}</span>
                </Powered>
              )}
              <ModalSecondaryButton type="button" onClick={back}>
                {t(`${prefix}.back`)}
              </ModalSecondaryButton>
            </>
          )
        }
        className="background-media-modal">
        <Panel>
          {step === "choose" && (
            <div className="choices">
              <button type="button" className="choice" onClick={() => setStep("upload")}>
                <span className="tile" aria-hidden="true">
                  <UploadIcon size={28} />
                </span>
                <span className="text">
                  <strong>{t(`${prefix}.own.${texts}`)}</strong>
                  <span>{t(`${prefix}.ownHint.${texts}`)}</span>
                </span>
                <Chevron />
              </button>
              {/* a background: Pexels' images or videos (its kind); the cover: either, each its own way in */}
              {(cover ? (["image", "video"] as Kind[]) : [kind]).map(each => (
                <button key={each} type="button" className="choice" onClick={() => openPexels(each)}>
                  <PexelsLogo size={56} />
                  <span className="text">
                    <strong>{t(`${prefix}.${cover ? `pexelsCover.${each}` : "pexels"}`)}</strong>
                    <span>{t(`${prefix}.pexelsHint.${each}`)}</span>
                  </span>
                  <Chevron />
                </button>
              ))}
            </div>
          )}

          {step === "upload" && <Dropzone kind={kind} texts={texts} both={cover} onFile={sendOwn} />}

          {step === "pexels" && (
            <PexelsBrowser kind={kind} siteId={siteId} onAdd={add} initial={search} landscape={cover} />
          )}
        </Panel>
      </Modal>
      {ownImage.cropModal}
      {pexelsImage.cropModal}
      {busy && <LoadingModal />}
    </>
  );
};

/** "›": the option leads somewhere. */
const Chevron = () => (
  <svg
    className="chevron"
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true">
    <path d="M9 6l6 6-6 6" />
  </svg>
);

const UploadIcon = ({ size = 28 }: { size?: number }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.8}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true">
    <path d="M12 16V4M7 9l5-5 5 5M4 16v3a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-3" />
  </svg>
);

/** Drop a file here, or click to pick one. */
const Dropzone = ({
  kind,
  texts,
  both,
  onFile,
}: {
  kind: Kind;
  /** Its texts' key: the kind's, or the cover's. */
  texts: string;
  /** Takes an image or a video (the cover). */
  both?: boolean;
  onFile: (file?: File) => void;
}) => {
  const { t } = useTranslation();
  const [over, setOver] = useState(false);
  const label = t(`${prefix}.drop.${texts}`);

  return (
    <label
      data-dropzone
      className={classNames("dropzone", { over })}
      onDragOver={event => {
        event.preventDefault();
        setOver(true);
      }}
      onDragLeave={() => setOver(false)}
      onDrop={event => {
        event.preventDefault();
        setOver(false);
        onFile(event.dataTransfer?.files?.[0]);
      }}>
      <input
        type="file"
        aria-label={label}
        accept={
          both
            ? "image/png,image/jpeg,image/webp,video/mp4,video/webm"
            : kind === "video"
              ? "video/mp4,video/webm"
              : "image/png,image/jpeg,image/webp"
        }
        onClick={event => ((event.target as HTMLInputElement).value = "")}
        onChange={event => onFile(event.target.files?.[0])}
      />
      <UploadIcon size={32} />
      <strong aria-hidden="true">{label}</strong>
      <span aria-hidden="true">{t(`${prefix}.ownHint.${texts}`)}</span>
    </label>
  );
};

/** Pexels' photos or videos: a search, popular terms, the results page after page, and their credit. */
const PexelsBrowser = ({
  kind,
  siteId,
  onAdd,
  initial = "",
  landscape,
}: {
  kind: Kind;
  siteId: string;
  onAdd: (item: PexelsItem) => void;
  initial?: string;
  /** Lying down (the cover): Pexels' landscape ones, in wide cells. */
  landscape?: boolean;
}) => {
  const { t, i18n } = useTranslation();
  // opens searched for the Keepo's field, written in the field so it can be changed or cleared
  const [input, setInput] = useState(initial);
  const [query, setQuery] = useState(initial.trim());
  const [items, setItems] = useState<PexelsItem[]>([]);
  const [nextPage, setNextPage] = useState<number | null>(null);
  // it searches as soon as it opens: loading from the first paint, never "nothing found" before the answer
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  // the last search asked: an older answer arriving late is dropped
  const asked = useRef(0);

  const load = useCallback(
    async (search: string, page: number) => {
      const ticket = ++asked.current;
      setLoading(true);
      setFailed(false);
      try {
        const answer = await api.pexels(
          siteId,
          kind === "video" ? "videos" : "photos",
          search,
          page,
          PEXELS_LOCALES[i18n.language?.slice(0, 2)],
          landscape ? "landscape" : undefined
        );
        if (ticket !== asked.current) return;
        setItems(current => (page === 1 ? answer.items : [...current, ...answer.items]));
        setNextPage(answer.nextPage);
      } catch {
        if (ticket === asked.current) setFailed(true);
      } finally {
        if (ticket === asked.current) setLoading(false);
      }
    },
    [kind, siteId, landscape, i18n.language]
  );

  // a search sent again (the same words too, to try again after a failure) asks again
  const [searchId, setSearchId] = useState(0);
  useEffect(() => {
    load(query, 1);
  }, [load, query, searchId]);

  const search = (term: string) => {
    setInput(term);
    setItems([]);
    setQuery(term.trim());
    setSearchId(id => id + 1);
  };

  const submit = (event: FormEvent) => {
    event.preventDefault();
    search(input);
  };

  // only full rows while more come: the rest shows with the next page (at the end, everything)
  // full rows only while there's more (the cover's rows have 3)
  const perRow = landscape ? 3 : PER_ROW;
  const shown = nextPage ? items.slice(0, items.length - (items.length % perRow)) : items;

  const popular = t(`${prefix}.popularTerms.${kind}`).split(", ");
  const credit = (item: PexelsItem) => t(`${prefix}.credit.${kind}`, { name: item.photographer });

  return (
    <div className={classNames("pexels", { landscape })}>
      <form className="search" role="search" onSubmit={submit}>
        <span className="field">
          <input
            type="search"
            value={input}
            aria-label={t(`${prefix}.search`)}
            placeholder={t(`${prefix}.search`)}
            onChange={event => setInput(event.target.value)}
          />
          {input && (
            <button type="button" className="clear" aria-label={t(`${prefix}.clearSearch`)} onClick={() => search("")}>
              <svg
                width="12"
                height="12"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={3}
                strokeLinecap="round"
                aria-hidden="true">
                <path d="M6 6l12 12M18 6L6 18" />
              </svg>
            </button>
          )}
        </span>
        <button type="submit">{t(`${prefix}.searchButton`)}</button>
      </form>
      <div className="popular">
        <span>{t(`${prefix}.popular`)}</span>
        {popular.map(term => (
          <button key={term} type="button" onClick={() => search(term)}>
            {term}
          </button>
        ))}
      </div>

      <div className="results">
        <div className="grid">
          {shown.map(item => {
            const name = item.alt || credit(item);
            return (
              <div key={item.id} className="item" style={{ backgroundColor: item.color ?? undefined }}>
                {/* the whole photo picks it; "Adicionar" shows on hover */}
                <button
                  type="button"
                  className="pick"
                  aria-label={`${t(`${prefix}.add`)}: ${name}`}
                  onClick={() => onAdd(item)}>
                  <img src={kind === "video" ? item.poster : item.thumb} alt={name} loading="lazy" />
                  {kind === "video" && <span className="duration">{duration(item.duration)}</span>}
                  <span className="cta" aria-hidden="true">
                    {t(`${prefix}.add`)}
                  </span>
                </button>
                <a className="credit" href={item.photographerUrl} target="_blank" rel="noreferrer">
                  {credit(item)}
                </a>
              </div>
            );
          })}
        </div>
        {loading && (
          // nothing yet: in the middle of the results; more: under the grid, where they'll show
          <div className={classNames("loading", { alone: items.length === 0 })}>
            <KeepoLoader size={24} />
          </div>
        )}
        {!loading && nextPage && (
          <div className="more">
            <button type="button" onClick={() => load(query, nextPage)}>
              {t(`${prefix}.loadMore`)}
            </button>
          </div>
        )}
        {!loading && failed && <p className="error">{t(`${prefix}.error`)}</p>}
        {!loading && !failed && items.length === 0 && <p className="empty">{t(`${prefix}.empty`)}</p>}
      </div>
    </div>
  );
};
